import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate, Link } from "react-router-dom";
import NavBar from '../components/NavBar';
import { Elements, useElements, useStripe, CardElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import SubscriptionPricing from '../components/SubscriptionPricing';
import AuthLogin from '../profile/auth-login';
import GoogleLogin, { GOOGLE_LOGIN_ENABLED } from '../components/GoogleLogin';
import '../assets/styles/Signup.css';
import { STRIPE_PUBLISHABLE_KEY } from '../config';

// Debug flag for authentication logging
const DEBUG_AUTH = process.env.REACT_APP_DEBUG_AUTH === 'true';

// Helper function for debug logging
const debugLog = (...args) => {
  if (DEBUG_AUTH) {
    console.log(...args);
  }
};

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

// Step 1: Account Creation Form
const AccountCreationForm = ({ formData, setFormData, onNext, onGoogleSignupData, googleSignupData }) => {
    const [errors, setErrors] = useState({});
    const [confirmPassword, setConfirmPassword] = useState('');
    
    // Username availability checking state
    const [usernameStatus, setUsernameStatus] = useState({
      checking: false,      // Currently checking availability
      available: null,      // true = available, false = taken, null = not checked yet
      error: null           // Error message if username is invalid or taken
    });
    const [usernameCheckTimeout, setUsernameCheckTimeout] = useState(null);

  
    const validatePassword = (password) => {
      return {
        length: password.length >= 8,
        uppercase: /[A-Z]/.test(password),
        special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
      };
    };

    /**
     * Check username availability with debouncing
     * Called when user types in the username field
     * Waits 500ms after user stops typing before making API call
     * @param {string} username - The username to check
     */
    const checkUsernameAvailability = (username) => {
      // Clear any pending timeout to debounce the API call
      if (usernameCheckTimeout) {
        clearTimeout(usernameCheckTimeout);
      }

      // Reset status if username is too short
      if (!username || username.length < 6) {
        setUsernameStatus({
          checking: false,
          available: null,
          error: username.length > 0 ? 'Username must be at least 6 characters' : null
        });
        return;
      }

      // Set checking state immediately for UI feedback
      setUsernameStatus(prev => ({ ...prev, checking: true, error: null }));

      // Debounce: Wait 500ms after user stops typing before checking
      const timeout = setTimeout(async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/check-username/${encodeURIComponent(username)}`
          );
          const data = await response.json();

          setUsernameStatus({
            checking: false,
            available: data.available,
            error: data.error || null
          });
        } catch (error) {
          console.error('Error checking username:', error);
          setUsernameStatus({
            checking: false,
            available: null,
            error: 'Failed to check username availability'
          });
        }
      }, 500);

      setUsernameCheckTimeout(timeout);
    };

    /**
     * Handle username input change
     * Updates form data and triggers availability check
     */
    const handleUsernameChange = (e) => {
      const newUsername = e.target.value;
      setFormData({ ...formData, username: newUsername });
      checkUsernameAvailability(newUsername);
    };
  
    const validateForm = () => {
      const newErrors = {};
      
      if (!formData.username || formData.username.length < 6) {
        newErrors.username = 'Username must be at least 6 characters';
      } else if (usernameStatus.available === false) {
        // Prevent submission if username is taken
        newErrors.username = 'This username is already taken';
      } else if (usernameStatus.checking) {
        // Prevent submission while still checking
        newErrors.username = 'Please wait while we check username availability';
      }
  
      // Skip password validation for Google users
      if (!googleSignupData) {
        // Enhanced password validation
        const passwordChecks = validatePassword(formData.password);
        if (!passwordChecks.length || !passwordChecks.uppercase || !passwordChecks.special) {
          newErrors.password = 'Password must contain 8 characters, one uppercase, and a special character (@#$%&)';
        }

        // Password confirmation validation
        if (formData.password !== confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
        }
      }
  
      if (!formData.firstName) {
        newErrors.firstName = 'First name is required';
      }
      if (!formData.lastName) {
        newErrors.lastName = 'Last name is required';
      }
      if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Valid email is required';
      }
  
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onNext();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="signup-form">
      <div className="form-group">
        <div className="username-input-container">
          <input
            type="text"
            placeholder="Username"
            value={formData.username}
            onChange={handleUsernameChange}
            className={`form-input ${usernameStatus.available === false ? 'error-input' : ''} ${usernameStatus.available === true ? 'success-input' : ''}`}
          />
          {/* Username availability indicator */}
          {formData.username.length >= 6 && (
            <span className="username-status-indicator">
              {usernameStatus.checking && <span className="status-checking">⏳</span>}
              {!usernameStatus.checking && usernameStatus.available === true && <span className="status-available">✅</span>}
              {!usernameStatus.checking && usernameStatus.available === false && <span className="status-taken">❌</span>}
            </span>
          )}
        </div>
        {/* Show error from form validation or username check */}
        {errors.username && <span className="error">{errors.username}</span>}
        {!errors.username && usernameStatus.error && <span className="error">{usernameStatus.error}</span>}
        {!errors.username && !usernameStatus.error && usernameStatus.available === true && (
          <span className="success-text">Username is available!</span>
        )}
      </div>

      {!googleSignupData && (
        <>
          <div className="form-group">
            <input
              type="password"
              placeholder="Password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="form-input"
            />
            {errors.password && <span className="error">{errors.password}</span>}
          </div>

          <div className="form-group">
            <input
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="form-input"
            />
            {errors.confirmPassword && <span className="error">{errors.confirmPassword}</span>}
          </div>
        </>
      )}
      
      {googleSignupData && (
        <div className="form-group">
          <div className="info-text" style={{ textAlign: 'center', color: '#28a745', fontWeight: 'bold', padding: '10px' }}>
            <span role="img" aria-label="checkmark">✅</span> You're signing up with Google - no password required!
          </div>
        </div>
      )}

      <div className="form-group">
        <input
          type="text"
          placeholder="First Name"
          value={formData.firstName}
          onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
          className={`form-input ${googleSignupData ? 'disabled-input' : ''}`}
          disabled={!!googleSignupData}
        />
        {errors.firstName && <span className="error">{errors.firstName}</span>}
        {googleSignupData && <span className="info-text">Auto-filled from Google</span>}
      </div>

      <div className="form-group">
        <input
          type="text"
          placeholder="Last Name"
          value={formData.lastName}
          onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
          className={`form-input ${googleSignupData ? 'disabled-input' : ''}`}
          disabled={!!googleSignupData}
        />
        {errors.lastName && <span className="error">{errors.lastName}</span>}
        {googleSignupData && <span className="info-text">Auto-filled from Google</span>}
      </div>

      <div className="form-group">
        <input
          type="email"
          placeholder="Email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className={`form-input ${googleSignupData ? 'disabled-input' : ''}`}
          disabled={!!googleSignupData}
        />
        {errors.email && <span className="error">{errors.email}</span>}
        {googleSignupData && <span className="info-text">Auto-filled from Google</span>}
      </div>

      <button type="submit" className="next-btn">Next</button>
      
      {GOOGLE_LOGIN_ENABLED && (
        <div style={{ margin: '20px 0', textAlign: 'center' }}>
          <p style={{ margin: '10px 0', color: '#666' }}>or</p>
          <GoogleLogin isSignup={true} onGoogleSignupData={onGoogleSignupData} />
        </div>
      )}
    </form>
  );
};

// Step 2: BillingForm with lazy-loaded Stripe
// In Signup.js

const BillingForm = ({ formData, setFormData, onBack, googleSignupData }) => {
const navigate = useNavigate();
const stripe = useStripe();
const elements = useElements();
const ctx = useContext(AuthLogin);

// Existing state
const [errors, setErrors] = useState({});
const [loading, setLoading] = useState(false);
const [promoCode, setPromoCode] = useState('');
const [stripeError, setStripeError] = useState(null);
const [agreedToTerms, setAgreedToTerms] = useState(false);
const [termsError, setTermsError] = useState('');
const [subscriptionType, setSubscriptionType] = useState('monthly');
// Track client IP for signup auditing
const [clientIp, setClientIp] = useState('');

useEffect(() => {
  // Best-effort fetch of public IP (server also captures IP independently)
  const fetchIp = async () => {
    try {
      const res = await fetch('https://api.ipify.org?format=json');
      const data = await res.json();
      if (data && data.ip) setClientIp(data.ip);
    } catch (_) {
      // Ignore failures; server will still capture IP
    }
  };
  fetchIp();
}, []);

// Add promo code state
const [promoResult, setPromoResult] = useState(null);
const [promoLoading, setPromoLoading] = useState(false);
const [promoMessage, setPromoMessage] = useState({ type: '', message: '' });

// Helper functions for login validation (copied from LoginInput.js)
const validateLogin = async (username, password) => {
  const url = `${process.env.REACT_APP_API_URL}/login`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error('Login validation failed');
    }

    return data;
  } catch (error) {
    throw error;
  }
};

const isFirstTimeLogin = async (user_id) => {
  const url = `${process.env.REACT_APP_API_URL}/isUserFirstTimeLogin/${user_id}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    }
  });

  if (!response.ok) {
    throw new Error('Failed to check first time login status');
  }

  const data = await response.text();
  return data;
};

const userAcceptedTermsAndConditions = async (user_id) => {
  const url = `${process.env.REACT_APP_API_URL}/userAcceptedTermsAndConditions/${user_id}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    }
  });

  if (!response.ok) {
    throw new Error('Failed to check terms and conditions status');
  }

  const data = await response.text();
  return data;
};

// Existing states list
const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

// Add function to validate promo code
const validatePromoCode = async () => {
  if (!promoCode.trim()) {
    setPromoMessage({ type: 'error', message: 'Please enter a promo code' });
    return;
  }
  
  setPromoLoading(true);
  setPromoMessage({ type: '', message: '' });
  
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/validate-promo-code/${promoCode.trim()}`);
    const data = await response.json();
    
    if (!response.ok) {
      setPromoMessage({ type: 'error', message: data.error || 'Failed to validate promo code' });
      setPromoResult(null);
    } else {
      setPromoResult(data.promo);
      
      // Handle different types of promo codes
      if (data.promo.is_trial) {
        setPromoMessage({ 
          type: 'success', 
          message: `${data.promo.trial_days}-day free trial will be applied!` 
        });
      } else if (data.promo.is_percentage) {
        setPromoMessage({ 
          type: 'success', 
          message: `${data.promo.discount_percentage}% discount will be applied!` 
        });
      } else {
        setPromoMessage({ 
          type: 'success', 
          message: `$${data.promo.discount_amount.toFixed(2)} discount will be applied!` 
        });
      }
    }
  } catch (error) {
    console.error('Error validating promo code:', error);
    setPromoMessage({ type: 'error', message: 'Failed to validate promo code' });
    setPromoResult(null);
  } finally {
    setPromoLoading(false);
  }
};

// Replace or modify the getPriceAmount function to include promo discount
const getPriceAmount = () => {
  const baseAmount = subscriptionType === 'monthly' ? 4.99 : 49.99;
  
  // If there's a valid promo code, apply the discount
  if (promoResult) {
    // If it's a free trial, return 0 for the first payment
    if (promoResult.is_trial && promoResult.trial_days > 0) {
      return 0;
    }
    // Apply percentage discount
    else if (promoResult.is_percentage) {
      const discountAmount = baseAmount * (promoResult.discount_percentage / 100);
      return (baseAmount - discountAmount).toFixed(2);
    }
    // Apply fixed amount discount
    else {
      return Math.max(0, baseAmount - promoResult.discount_amount).toFixed(2);
    }
  }
  
  return baseAmount;
};

// Updated validateForm to include payment validation
const validateForm = () => {
    const newErrors = {};
    
    if (!formData.street) newErrors.street = 'Street address is required';
    if (!formData.city) newErrors.city = 'City is required';
    if (!formData.state) newErrors.state = 'State is required';
    if (!formData.zipCode) newErrors.zipCode = 'ZIP code is required';
    if (!formData.zipCode.match(/^\d{5}$/)) newErrors.zipCode = 'Invalid ZIP code format';
    if (!agreedToTerms) {
    setTermsError('You must accept the terms and conditions to continue');
    return false;
    }

    setErrors(newErrors);
    setTermsError('');
    return Object.keys(newErrors).length === 0;
};

// Updated handleSubmit with Stripe integration
const handleSubmit = async (e) => {
    e.preventDefault();
    setStripeError(null);

    if (!validateForm() || !stripe || !elements) {
      return;
    }

    setLoading(true);

        // Before loadStripe
    // console.log("About to initialize Stripe with key:", stripeKey);

    try {
      const { error: cardError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
        billing_details: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          address: {
            line1: formData.street,
            line2: formData.unit || undefined,
            city: formData.city,
            state: formData.state,
            postal_code: formData.zipCode,
            country: 'US'
          }
        }
      });

      if (cardError) {
        throw new Error(cardError.message);
      }

      // Add promo code to request if available
      const requestBody = {
        username: formData.username,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        street: formData.street,
        unit: formData.unit,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        paymentMethodId: paymentMethod.id,
        subscriptionType,
        amount: getPriceAmount(),
        signedUpWithIp: clientIp || undefined
      };
      
      // Add Google OAuth data if this is a Google signup
      if (googleSignupData) {
        requestBody.isGoogleUser = true;
        requestBody.googleId = googleSignupData.googleId;
        requestBody.profilePicture = googleSignupData.profilePicture;
        requestBody.oauthProvider = 'google';
        // For Google users, don't require password
        delete requestBody.password;
      }
      
      // Add promo code data if available
      if (promoResult) {
        requestBody.promoCode = promoCode;
        requestBody.promoId = promoResult.id;
      }
      
      // Add referral code if available
      if (formData.referralCode) {
        requestBody.referralCode = formData.referralCode;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Signup failed');
      }

      // Only confirm payment if there's a client secret (non-trial subscriptions)
      if (data.clientSecret) {
        const { error: confirmError } = await stripe.confirmCardPayment(data.clientSecret);
        if (confirmError) {
          throw new Error(confirmError.message);
        }
      }

      // Automatically log in the user after successful signup
      try {
        let userData;
        
        if (googleSignupData) {
          // For Google OAuth users, use a different login method
          const googleLoginResponse = await fetch(`${process.env.REACT_APP_API_URL}/auth/google/login-existing`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
              email: formData.email,
              googleId: googleSignupData.googleId 
            }),
          });
          
          const googleData = await googleLoginResponse.json();
          
          if (googleData.success && googleData.user) {
            userData = [googleData.user];
          } else {
            throw new Error('Google user login failed after signup');
          }
        } else {
          // Regular user login
          userData = await validateLogin(formData.username, formData.password);
        }
        
        if (userData[0]) {
          const firstTimeLogin = await isFirstTimeLogin(userData[0].user_id);
          const userAcceptedTAndC = await userAcceptedTermsAndConditions(userData[0].user_id);

          // Log the user in automatically
          ctx.onLogin(
            userData[0].username, 
            userData[0].firstname + " " + userData[0].lastname,
            userData[0].role, 
            userData[0].user_id, 
            firstTimeLogin, 
            userAcceptedTAndC, 
            googleSignupData ? null : userData[0].password, // No password for Google users
            userData[0].issubscribed || userData[0].isSubscribed
          );

          // Show success message and redirect to dashboard
          let successMessage = data.appliedTrial 
            ? `Welcome! Your ${data.appliedTrial.days}-day free trial has started.`
            : 'Welcome to FinnTech! Your account has been created successfully.';
            
          // Add referral message if a referral was applied
          if (data.referralApplied) {
            successMessage = 'Welcome to FinnTech! Your first month is free thanks to a friend\'s referral.';
          }

          navigate('/my-dashboard', { 
            state: { 
              message: successMessage
            }
          });
        }
      } catch (loginError) {
        console.error('Auto-login failed:', loginError);
        // Fallback to manual login if auto-login fails
        let successMessage = data.appliedTrial 
          ? `Account created successfully! Your ${data.appliedTrial.days}-day free trial has started. Please log in.`
          : 'Account created successfully! Please log in.';
          
        if (data.referralApplied) {
          successMessage = 'Account created successfully! Your first month is free thanks to a friend\'s referral. Please log in.';
        }

        navigate('/login', { 
          state: { 
            message: successMessage
          }
        });
      }

    } catch (error) {
      console.error('Payment Error:', error);
      setStripeError(error.message);
      if (elements) {
        elements.getElement(CardElement).clear();
      }
    } finally {
      setLoading(false);
    }
};

return (
    <form onSubmit={handleSubmit} className="signup-form">
    <div className="form-section">
        <SubscriptionPricing 
        selectedType={subscriptionType}
        onTypeChange={setSubscriptionType}
        />

        <h3>Billing Address</h3>
        <div className="form-group">
        <input
            type="text"
            placeholder="Street Address"
            value={formData.street}
            onChange={(e) => setFormData({ ...formData, street: e.target.value })}
            className={`form-input ${errors.street ? 'error-input' : ''}`}
            disabled={loading}
        />
        {errors.street && <span className="error">{errors.street}</span>}
        </div>

        <div className="form-group">
        <input
            type="text"
            placeholder="Unit/Apartment (optional)"
            value={formData.unit}
            onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
            className="form-input"
            disabled={loading}
        />
        </div>

        <div className="form-row">
        <div className="form-group half">
            <input
            type="text"
            placeholder="City"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            className={`form-input ${errors.city ? 'error-input' : ''}`}
            disabled={loading}
            />
            {errors.city && <span className="error">{errors.city}</span>}
        </div>

        <div className="form-group half">
            <select
            value={formData.state}
            onChange={(e) => setFormData({ ...formData, state: e.target.value })}
            className={`form-input form-select ${errors.state ? 'error-input' : ''}`}
            disabled={loading}
            >
            <option value="">Select State</option>
            {states.map(state => (
                <option key={state} value={state}>{state}</option>
            ))}
            </select>
            {errors.state && <span className="error">{errors.state}</span>}
        </div>
        </div>

        <div className="form-row">
        <div className="form-group half">
            <input
            type="text"
            placeholder="ZIP Code"
            value={formData.zipCode}
            onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
            className={`form-input ${errors.zipCode ? 'error-input' : ''}`}
            maxLength="5"
            disabled={loading}
            />
            {errors.zipCode && <span className="error">{errors.zipCode}</span>}
        </div>
        
        <div className="form-group half">
            <input
            type="text"
            value="United States"
            readOnly
            disabled
            className="form-input disabled-input"
            />
            <span className="info-text">Currently only accepting U.S. customers</span>
        </div>
        </div>
    </div>

    <div className="form-section">
        <h3>Payment Information</h3>
        <div className="promo-code-container">
          <h3>Promo Code</h3>
          <div className="promo-code-input">
            <input
              type="text"
              placeholder="Enter promo code (optional)"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              disabled={promoLoading || promoResult !== null}
            />
            <button 
              type="button" 
              onClick={validatePromoCode}
              disabled={promoLoading || promoResult !== null}
              className="promo-validate-btn"
            >
              {promoLoading ? 'Validating...' : 'Apply'}
            </button>
            {promoResult && (
              <button 
                type="button" 
                onClick={() => {
                  setPromoCode('');
                  setPromoResult(null);
                  setPromoMessage({ type: '', message: '' });
                }}
                className="promo-remove-btn"
              >
                Remove
              </button>
            )}
          </div>
          
          {promoMessage.message && (
            <div className={`promo-message ${promoMessage.type}`}>
              {promoMessage.message}
            </div>
          )}
        </div>
        
        <div className="card-element-container">
        <CardElement options={{
            style: {
            base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                color: '#aab7c4',
                },
            },
            invalid: {
                color: '#9e2146',
            },
            },
        }} />
        </div>
    </div>

    {stripeError && (
        <div className="error-message">
        {stripeError}
        </div>
    )}

        {/* Add subscription summary when promo is applied */}
        {promoResult && (
        <div className="subscription-summary">
            <h4>Subscription Summary</h4>
            {promoResult.is_trial ? (
                <>
                    <div className="summary-row">
                        <span>Free Trial Period:</span>
                        <span>{promoResult.trial_days} days</span>
                    </div>
                    <div className="summary-row">
                        <span>First Payment Date:</span>
                        <span>{new Date(Date.now() + promoResult.trial_days * 24 * 60 * 60 * 1000).toLocaleDateString()}</span>
                    </div>
                    <div className="summary-row total">
                        <span>Today's Payment:</span>
                        <span>$0.00</span>
                    </div>
                </>
            ) : (
                <>
                    <div className="summary-row">
                        <span>Base Price:</span>
                        <span>${subscriptionType === 'monthly' ? '4.99' : '49.99'}</span>
                    </div>
                    <div className="summary-row discount">
                        <span>Discount ({promoResult.is_percentage ? `${promoResult.discount_percentage}%` : 'Fixed'}):</span>
                        <span>-${promoResult.is_percentage 
                            ? ((subscriptionType === 'monthly' ? 4.99 : 49.99) * promoResult.discount_percentage / 100).toFixed(2) 
                            : Math.min(promoResult.discount_amount, (subscriptionType === 'monthly' ? 4.99 : 49.99)).toFixed(2)}</span>
                    </div>
                    <div className="summary-row total">
                        <span>Total:</span>
                        <span>${getPriceAmount()}</span>
                    </div>
                </>
            )}
        </div>
    )}

  <div className="form-section terms-section">
        <h3>Terms & Conditions</h3>
        <div className="terms-content">
        <div className="terms-checkbox-container">
            <input
            type="checkbox"
            id="terms"
            checked={agreedToTerms}
            onChange={(e) => {
                setAgreedToTerms(e.target.checked);
                if (e.target.checked) setTermsError('');
            }}
            className="terms-checkbox"
            disabled={loading}
            />
            <label htmlFor="terms" className="terms-label">
            I have read and agree to the <Link to="/terms-and-conditions" target="_blank" className="terms-link">Terms and Conditions</Link>
            </label>
        </div>
        {termsError && <span className="error">{termsError}</span>}
        </div>
    </div>

    <div className="button-group">
        <button 
        type="button" 
        onClick={onBack} 
        className="back-btn"
        disabled={loading}
        >
        Back
        </button>
        <button 
        type="submit" 
        className="submit-btn"
        disabled={loading}
        >
        {loading ? 'Processing...' : 'Complete Signup'}
        </button>
    </div>

    </form>
);
};

const Signup = () => {
    const location = useLocation();
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
      username: '',
      password: '',
      firstName: '',
      lastName: '',
      email: '',
      street: '',
      unit: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
      referralCode: ''
    });
    const [googleSignupData, setGoogleSignupData] = useState(null);
    
    // Handle Google signup data and auto-fill form
    const handleGoogleSignupData = (googleData) => {
      setGoogleSignupData(googleData);
      setFormData(prevData => ({
        ...prevData,
        firstName: googleData.firstName,
        lastName: googleData.lastName,
        email: googleData.email,
        // Generate a username from email for Google users
        username: googleData.email.split('@')[0] + '_google'
      }));
    };
    
    // Handle Google user data from navigation state (when redirected from login)
    useEffect(() => {
      const state = location.state;
      if (state && state.isGoogleSignup && state.googleUserData) {
        debugLog('🔍 FRONTEND DEBUG: Google user data received from navigation:', state.googleUserData);
        
        // Set Google signup data
        setGoogleSignupData(state.googleUserData);
        
        // Pre-fill form with Google user data
        setFormData(prevData => ({
          ...prevData,
          firstName: state.googleUserData.firstName,
          lastName: state.googleUserData.lastName,
          email: state.googleUserData.email,
          username: state.googleUserData.email.split('@')[0] + '_google'
        }));
        
        debugLog('✅ FRONTEND DEBUG: Form pre-filled with Google data');
      }
    }, [location.state]);
    
    // Extract referral code from URL query parameters
    useEffect(() => {
      const queryParams = new URLSearchParams(location.search);
      const refCode = queryParams.get('ref');
      
      if (refCode) {
        setFormData(prevData => ({
          ...prevData,
          referralCode: refCode
        }));
      }
    }, [location.search]);
  
    // Stripe configuration options
    const stripeOptions = {
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: '#000000',
        },
      },
    };
  
    return (
      <>
        <NavBar />
        <div className="signup-container">
          {/* Show Google signup banner if user was redirected from Google login */}
          {googleSignupData && (
            <div className="google-signup-banner">
              <p><span role="img" aria-label="checkmark">✅</span> Google account verified! Please complete your subscription setup below.</p>
            </div>
          )}
          
          <div className="progress-indicator">
            <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
              Account
            </div>
            <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
              Billing
            </div>
          </div>
  
          {currentStep === 1 && (
            <AccountCreationForm
              formData={formData}
              setFormData={setFormData}
              onNext={() => setCurrentStep(2)}
              onGoogleSignupData={handleGoogleSignupData}
              googleSignupData={googleSignupData}
            />
          )}
  
          {currentStep === 2 && (
            <Elements stripe={stripePromise} options={stripeOptions}>
              <BillingForm
                formData={formData}
                setFormData={setFormData}
                onBack={() => setCurrentStep(1)}
                googleSignupData={googleSignupData}
              />
            </Elements>
          )}
          
          {/* Display referral banner if coming from a referral */}
          {formData.referralCode && (
            <div className="referral-banner">
              <p>You've been referred by a friend! Your first month's subscription will be free.</p>
            </div>
          )}
        </div>
      </>
    );
  };
  
  export default Signup;